import React from "react";
import { func, bool, object, string, exact } from "prop-types";
import { useForm } from "react-hook-form";
import { useTranslation, Trans } from "@lib/i18n";
import getConfig from "next/config";

import EditableContainer from "@components/shared/EditableContainer";
import TextField from "@components/shared/TextField";
import TelephoneField from "@components/shared/TelephoneField";
import Typography from "@components/shared/Typography";
import SelectField from "@components/shared/SelectField";
import { Button } from "@components/shared/Button";
import SimpleButton from "@components/shared/SimpleButton";
import { useIsB2BSelected } from "@hooks/useIsB2BSelected";
import { getIsTINRelevantForCountry } from "@shared/v2/lib/getIsTINRelevantForCountry";
import { getIsCountry } from "@lib/getIsCountry";
import { useIsTelephoneFieldEnabledSelector } from "@redux/reducers/appConfig";
import { COUNTRIES, USER_FIELDS_NAMES } from "@shared/constants";
import { getCivilityOptions } from "lib/getCivilityOptions";

const { publicRuntimeConfig } = getConfig();
const { COUNTRY } = publicRuntimeConfig;

const { PERSONAL_INFORMATION } = USER_FIELDS_NAMES;

const PersonalInformation = ({
  onIsEditingChange,
  isEditing,
  regexPatterns,
  onSubmit,
  data: personalDetail,
  onChangeEmailButtonClick,
  isEditButtonDisabled = false,
}) => {
  const { t } = useTranslation();

  const isB2BSelected = useIsB2BSelected();

  const isTINRelevantForSelectedCountry = getIsTINRelevantForCountry(COUNTRY);

  const isTelephoneFieldEnabled = useIsTelephoneFieldEnabledSelector();

  const { register, handleSubmit, reset } = useForm({
    defaultValues: personalDetail,
  });

  const civilityOptions = getCivilityOptions(t);

  const hideBecauseESAndB2B = isB2BSelected && getIsCountry(COUNTRIES.ES);

  return (
    <EditableContainer
      title={<Trans i18nKey={"myDetails.personalDetail"} />}
      isEditing={isEditing}
      onChange={onIsEditingChange}
      isDisabled={isEditButtonDisabled}
      summary={
        <div>
          <Typography className="text-sm">
            {t(`myDetails.inputs.civilityLabels.${personalDetail.civility}`)}
          </Typography>
          <Typography className="text-sm">
            {personalDetail.userFirstName}
          </Typography>
          <Typography className="text-sm">
            {personalDetail.userLastName}
          </Typography>
          <Typography className="text-sm">{personalDetail.phone}</Typography>

          {isTINRelevantForSelectedCountry && !hideBecauseESAndB2B && (
            <Typography className="text-sm">{personalDetail.tin}</Typography>
          )}
          <Typography className="text-sm">{personalDetail.email}</Typography>
        </div>
      }
      form={
        <form
          className="flex flex-col items-center w-full"
          onSubmit={handleSubmit(data => {
            onSubmit(data);
            reset(data);
          })}
        >
          <SelectField
            required
            fullWidth
            name={PERSONAL_INFORMATION.CIVILITY}
            ref={register}
            options={civilityOptions}
            label={t(`myDetails.inputs.${PERSONAL_INFORMATION.CIVILITY}`)}
          />
          <TextField
            required
            fullWidth
            ref={register}
            name={PERSONAL_INFORMATION.USER_FIRST_NAME}
            label={t(
              `myDetails.inputs.${PERSONAL_INFORMATION.USER_FIRST_NAME}`
            )}
            pattern={regexPatterns[PERSONAL_INFORMATION.USER_FIRST_NAME]}
            errorHelperMessage={t(
              `myDetails.helperTexts.${PERSONAL_INFORMATION.USER_FIRST_NAME}`
            )}
            placeholder={t(
              `myDetails.placeholders.${PERSONAL_INFORMATION.USER_FIRST_NAME}`
            )}
          />
          <TextField
            required
            fullWidth
            ref={register}
            name={PERSONAL_INFORMATION.USER_LAST_NAME}
            label={t(`myDetails.inputs.${PERSONAL_INFORMATION.USER_LAST_NAME}`)}
            pattern={regexPatterns[PERSONAL_INFORMATION.USER_LAST_NAME]}
            errorHelperMessage={t(
              `myDetails.helperTexts.${PERSONAL_INFORMATION.USER_LAST_NAME}`
            )}
            placeholder={t(
              `myDetails.placeholders.${PERSONAL_INFORMATION.USER_LAST_NAME}`
            )}
          />
          {isTelephoneFieldEnabled ? (
            <TelephoneField
              required
              fullWidth
              ref={register}
              name={PERSONAL_INFORMATION.PHONE}
              label={t(`myDetails.inputs.${PERSONAL_INFORMATION.PHONE}`)}
              pattern={regexPatterns[PERSONAL_INFORMATION.PHONE]}
              errorHelperMessage={t(
                `myDetails.helperTexts.${PERSONAL_INFORMATION.PHONE}`
              )}
              placeholder={t(
                `myDetails.placeholders.${PERSONAL_INFORMATION.PHONE}`
              )}
            />
          ) : (
            <TextField
              required
              fullWidth
              ref={register}
              name={PERSONAL_INFORMATION.PHONE}
              label={t(`myDetails.inputs.${PERSONAL_INFORMATION.PHONE}`)}
              pattern={regexPatterns[PERSONAL_INFORMATION.PHONE]}
              errorHelperMessage={t(
                `myDetails.helperTexts.${PERSONAL_INFORMATION.PHONE}`
              )}
              placeholder={t(
                `myDetails.placeholders.${PERSONAL_INFORMATION.PHONE}`
              )}
            />
          )}
          {isTINRelevantForSelectedCountry && !hideBecauseESAndB2B && (
            <TextField
              required
              fullWidth
              ref={register}
              name={PERSONAL_INFORMATION.TIN}
              label={t(`myDetails.inputs.${PERSONAL_INFORMATION.TIN}`)}
              pattern={regexPatterns[PERSONAL_INFORMATION.TIN]}
              errorHelperMessage={t(
                `myDetails.helperTexts.${PERSONAL_INFORMATION.TIN}`
              )}
              placeholder={t(
                `myDetails.placeholders.${PERSONAL_INFORMATION.TIN}`
              )}
            />
          )}
          <TextField
            required
            fullWidth
            disabled
            value={personalDetail.email}
            name={PERSONAL_INFORMATION.EMAIL}
            label={t(`myDetails.inputs.${PERSONAL_INFORMATION.EMAIL}`)}
            type="email"
            errorHelperMessage={t(
              `myDetails.helperTexts.${PERSONAL_INFORMATION.EMAIL}`
            )}
            placeholder={t(
              `myDetails.placeholders.${PERSONAL_INFORMATION.EMAIL}`
            )}
          />
          <SimpleButton
            className="text-xs -mt-2 self-end"
            onClick={onChangeEmailButtonClick}
          >
            {t("myDetails.changeEmail")}
          </SimpleButton>
          <Button className="text-sm mt-8 mb-2" size="small" type="submit">
            {t("myDetails.inputs.save")}
          </Button>
        </form>
      }
    />
  );
};

PersonalInformation.propTypes = {
  onSubmit: func.isRequired,
  onChangeEmailButtonClick: func.isRequired,
  regexPatterns: exact({
    [PERSONAL_INFORMATION.USER_FIRST_NAME]: string,
    [PERSONAL_INFORMATION.USER_LAST_NAME]: string,
    [PERSONAL_INFORMATION.PHONE]: string,
    [PERSONAL_INFORMATION.TIN]: string,
  }).isRequired,
  onIsEditingChange: func,
  isEditing: bool,
  fontSizeSummary: string,
  data: object,
  isEditButtonDisabled: bool,
};

export default PersonalInformation;
